
import { createClient } from '@supabase/supabase-js';

// Supabase-Konfiguration aus Umgebungsvariablen
const supabaseUrl = import.meta.env.VITE_SUPABASE_URL || "https://brripffladqztihvuxts.supabase.co";
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY || "eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6ImJycmlwZmZsYWRxenRpaHZ1eHRzIiwicm9sZSI6ImFub24iLCJpYXQiOjE3NDExNjgwNDQsImV4cCI6MjA1Njc0NDA0NH0.UhMgZ036bp1lfPKAnEcAd2QVXj8LQVCOutOVhd_LII0";

// Supabase-Client erstellen
const supabase = createClient(supabaseUrl, supabaseAnonKey);

export interface EmailAttachment {
  content: string;  // Base64-kodierter Inhalt
  filename: string;
  type: string;     // MIME-Typ, z.B. "application/pdf"
  disposition: "attachment" | "inline";
}

export interface EmailData {
  to: string | string[];
  subject: string;
  body: string;
  from?: string;
  replyTo?: string;
  attachments?: EmailAttachment[];
}

/**
 * E-Mail über Supabase Edge Function senden
 */
export const sendEmail = async (emailData: EmailData): Promise<{ success: boolean; message: string }> => {
  try {
    // Standardwerte setzen mit korrigiertem Firmennamen
    const payload = {
      to: emailData.to,
      subject: emailData.subject,
      body: emailData.body,
      from: emailData.from || 'Hoss Immo Consulting <work@hoss-immo.de>',
      ...(emailData.replyTo && { replyTo: emailData.replyTo }),
      ...(emailData.attachments && { attachments: emailData.attachments })
    };
    
    console.log('Sende E-Mail via Edge Function an:', payload.to);
    console.log('E-Mail-Betreff:', payload.subject);
    
    // Edge Function aufrufen
    const { data, error } = await supabase.functions.invoke('send-email', {
      body: payload
    });

    if (error) {
      console.error('Fehler beim E-Mail-Versand:', error);
      return { 
        success: false, 
        message: 'Fehler beim Senden der E-Mail: ' + error.message 
      };
    }

    console.log('E-Mail erfolgreich gesendet:', data);
    return { success: true, message: 'E-Mail erfolgreich gesendet' };
  } catch (error) {
    console.error('Unerwarteter Fehler beim E-Mail-Versand:', error);
    return { 
      success: false, 
      message: 'Unerwarteter Fehler beim E-Mail-Versand: ' + error.message 
    };
  }
};

// Hilfsfunktion zur Konvertierung von Dateien in Base64 für Anhänge
export const fileToAttachment = async (file: File): Promise<EmailAttachment> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    
    reader.onload = () => {
      // Das Ergebnis enthält das "data:MIME;base64," Präfix, das entfernt werden muss
      const content = reader.result as string;
      const base64Content = content.split(',')[1];
      
      resolve({
        content: base64Content,
        filename: file.name,
        type: file.type,
        disposition: "attachment"
      });
    };
    
    reader.onerror = () => {
      reject(new Error('Fehler beim Lesen der Datei'));
    };
    
    reader.readAsDataURL(file);
  });
};

/**
 * Alternative direkte Methode, falls die Edge Function nicht funktioniert
 */
export const sendEmailDirect = async (emailData: EmailData): Promise<{ success: boolean; message: string }> => {
  try {
    // Diese Methode würde direkt einen Endpunkt in deinem Backend aufrufen
    // oder einen E-Mail-Service wie SendGrid/Mailjet/etc. direkt ansprechen
    
    // Beispiel für direkten Aufruf eines Backend-Endpunkts
    const response = await fetch('/api/send-email', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        to: emailData.to,
        subject: emailData.subject,
        body: emailData.body,
        from: 'Hoss Immo Consulting <work@hoss-immo.de>',
        ...(emailData.replyTo && { replyTo: emailData.replyTo })
      }),
    });

    const result = await response.json();
    
    if (!response.ok) {
      throw new Error(result.message || 'Fehler beim Senden der E-Mail');
    }

    return { success: true, message: 'E-Mail erfolgreich gesendet' };
  } catch (error) {
    console.error('Fehler bei direktem E-Mail-Versand:', error);
    return { 
      success: false, 
      message: 'Fehler beim direkten Senden der E-Mail: ' + error.message 
    };
  }
};
